import React, { useContext, useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import InvoiceStatusHelper from '../helpers/InvoiceStatusHelper'
import InvoiceStatus from './InvoiceStatus'
import DirectDebitConsentApi from '../services/api/DirectDebitConsent'
import OneClick from './OneClick'
import BankIDSign from './BankIDSign'
import BankValidationApi from '../services/api/BankValidation'
import LoadingComponent from './LoadingComponent'
import Context from '../context/Context'
import OneClickPaymentConsentTerms from './OneClickPaymentConsentTerms'
import SigningChoice from './SigningChoice'
import { BackButton } from '../utils/Elements'
import { useTranslation } from 'react-i18next'

const DirectDebitConsent = ({ bankIDCollectResponse, formValues }) => {
  const context = useContext(Context)
  const [isConsentCreated, setIsConsentCreated] = useState(false)
  const [isFormDataValid, setIsFormDataValid] = useState(false)
  const [addModalShow, setAddModalShow] = useState(false)
  const [stateFormValues, setStateFormValues] = useState({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (
      bankIDCollectResponse &&
      Object.hasOwn(bankIDCollectResponse, 'status') &&
      bankIDCollectResponse.status === 'complete'
    ) {
      setLoading(true)
      createConsent(context.invoice, formValues)
    }

    return () => null
  }, [])

  function validateBank (values, actions) {
    const bankValidationApi = new BankValidationApi()

    bankValidationApi.post(
      context.customer.id,
      values.clearingNumber,
      values.bankAccount
    )
      .then(res => res.json())
      .then(data => {
        if (data.short_code === '400') {
          actions.setErrors({ msg: data.message })
          setIsFormDataValid(false)
          actions.setSubmitting(false)
        } else {
          if (data.isValid === '1') {
            setIsFormDataValid(true)
            setStateFormValues(values)
            actions.setSubmitting(true)
          } else {
            setIsFormDataValid(false)
            actions.setSubmitting(false)
          }
        }
      })
      .catch(error => {
        actions.setErrors({ msg: error.toString() })
      })
  }

  function createConsent (invoice, values) {
    const directDebitConsent = new DirectDebitConsentApi()

    directDebitConsent.post(
      bankIDCollectResponse.completionData.user.personalNumber,
      values.clearingNumber,
      values.bankAccount,
      invoice.ocr,
      bankIDCollectResponse.bankIdLogId
    )
      .then(res => res.json())
      .then(data => {
        if (data.short_code === '201') {
          setLoading(false)
          setIsConsentCreated(true)
        } else {
          if (Object.keys(data).includes('add_on_code')) {
            let errorMsg = ''
            Object.entries(data.add_on_code).forEach(([key, value]) => {
              errorMsg = value
            })

            if (errorMsg !== '') {
              console.error(errorMsg)
            }
          }

          setError(t(data.message))
        }
      })
  }

  const TermsModalLink = () => {
    return (
      <span className='modal-link'
        title='Visa medgivande villkor'
        alt='Villkor'
        onClick={() => setAddModalShow(true)}>
        Villkor
      </span>
    )
  }

  if (isConsentCreated) {
    return <OneClick isConsentCreated={true} />
  }

  if (error !== '') {
    return (
      <div>
        <div className="row mt-2 m-auto">
          <div className="col-12 text-center text-danger">
            <p>{ error }</p>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-12 text-center">
            <BackButton style={context.style} />
          </div>
        </div>
      </div>
    )
  }

  if (
    bankIDCollectResponse && bankIDCollectResponse.status === 'complete' &&
    loading
  ) {
    return <LoadingComponent />
  } else if (isFormDataValid) {
    const textToSign = `Jag godkänner att ett medgivande tecknas för ${context.organizationSettings.organizationName}` +
      ` med följande uppgifter:\r\n\r\nClearingnummer: ${stateFormValues.clearingNumber}` +
      `\r\nKontonummer: ${stateFormValues.bankAccount}`

    return <BankIDSign
      textToSign={textToSign}
      whatIsSigned={'directDebitConsent'}
      formValues={stateFormValues}
    />
  } else {
    const InfoText = () => {
      return (
        <div className="row mt-2">
          <div className="col-8 offset-2 text-center">
            <h1>Uppgifter för betalning</h1>
            <span className="paragraph-regular-14">
              Vänligen ange vilket konto du vill att pengarna ska dras ifrån. Vi kommer att ansöka om ett
              medgivande hos din bank. <TermsModalLink />
            </span>

            <OneClickPaymentConsentTerms
              show={addModalShow}
              onHide={() => setAddModalShow(false)}
            />
          </div>
        </div>
      )
    }

    const onlyDigits = /^[0-9]*$/
    const ConsentSchema = Yup.object().shape({
      clearingNumber: Yup.string()
        .trim()
        .min(4, 'Clearingnumret ska bestå av 4 eller 5 siffror')
        .max(5, 'Clearingnumret ska bestå av 4 eller 5 siffror')
        .matches(onlyDigits, 'Endast siffror är tillåtna')
        .required('Clearingnummer måste anges'),
      bankAccount: Yup.string()
        .trim()
        .min(5, 'Felaktigt antal tecken')
        .max(15, 'Felaktigt antal tecken')
        .matches(onlyDigits, 'Endast siffror är tillåtna')
        .required('Kontonummer måste anges')
    })

    if (InvoiceStatusHelper.shouldInvoiceBePaid(context.invoice)) {
      return (
        <div>
          <InfoText />
          <div className="mt-3 d-flex h-100">
            <Formik
              initialValues={{
                clearingNumber: '',
                bankAccount: '',
                signingActionId: ''
              }}
              validationSchema={ConsentSchema}
              onSubmit={ (values, actions) => validateBank(values, actions) }>
              {({ errors, isSubmitting, setFieldValue }) => (
                <div className="row mt-2 m-auto">
                  <div className="col-12 text-center">
                    <Form>
                      <div className="form-group">
                        <label htmlFor="clearingNumber">Clearingnummer</label>
                        <Field type="text" name="clearingNumber" placeholder="1234" className="form-control directDebitConsentElement" />
                        <ErrorMessage name="clearingNumber" render={msg => <div className="consentValidationError">{msg}</div>} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="bankAccount">Kontonummer</label>
                        <Field type="text" name="bankAccount" placeholder="123456789" className="form-control directDebitConsentElement" />
                        <ErrorMessage name="bankAccount" render={msg => <div className="consentValidationError">{msg}</div>} />
                      </div>
                      <div className="form-group">
                        {errors && errors.msg && <div className="consentValidationError">{errors.msg}</div>}
                      </div>

                      <Field type="hidden" name="signingActionId" value="1" />

                      <SigningChoice isSubmitting={isSubmitting} onClickAction={(event) => {
                        if (['SPAN', 'IMG'].indexOf(event.target.tagName) !== -1) {
                          setFieldValue('signingActionId', event.target.parentElement.dataset.value)
                        } else {
                          setFieldValue('signingActionId', event.target.dataset.value)
                        }
                      }} />

                      <div className="row mt-2">
                        <div className="col-12 text-center">
                          <BackButton style={context.style} />
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              )}
            </Formik>
          </div>
        </div>
      )
    } else {
      return <InvoiceStatus />
    }
  }
}

DirectDebitConsent.propTypes = {
  bankIDCollectResponse: PropTypes.object,
  formValues: PropTypes.object
}

export default DirectDebitConsent
